/**
 * Trade-aware pricing intelligence.
 *
 * Two pure functions used by the Quote Calculator:
 *   1. suggestLaborRate(slug)  — typical 2026 hourly rate for that trade.
 *   2. suggestMarkup(slug, subtotal)  — optimal markup % given trade + job size.
 *
 * These are deliberately frontend-only (no backend round-trip) so the
 * marketing-page calculator can use them for guests too. Numbers come from
 * 2024-2026 industry surveys (HomeAdvisor, Angi, NEXT Insurance) — they're
 * not magic, just sane defaults the user can always override.
 */

// ─── Per-trade typical labor rates ($/hr) ──────────────────────────────
//
// Roofing/Painting are surprisingly low because workers are usually paid
// less than skilled electricians/plumbers. These are crew rates, not
// licensed-master rates.
export const TRADE_LABOR_RATES = {
  'interior-paint':    50,
  'exterior-paint':    55,
  'cabinet-resurface': 65,
  'drywall-repair':    55,
  'deck-staining':     50,
  'bathtub-resurface': 60,
  'bathtub-enclosure': 65,
  'counter-tops':      70,
  'electrician':       85,
  'plumbing':          95,
  'roofing':           65,
  'windows':           75,
  'lawn-care':         45,
  'cleaning':          35,
  'special':           60,
  'custom':            50,
};

export const suggestLaborRate = (slug) =>
  TRADE_LABOR_RATES[slug] ?? 50;

// ─── Markup optimizer ──────────────────────────────────────────────────
//
// We score each trade with a "base markup" range and shade UP for small jobs
// (overhead doesn't scale linearly) and DOWN for very large jobs (volume
// discount = win-rate boost). The output is a single percent.
//
// Rationale baked into the return value so the UI can explain the number.
const TRADE_BASE_MARKUP = {
  // Trade           [floor, ceiling, sweet-spot]
  'interior-paint':    [20, 30, 25],
  'exterior-paint':    [22, 32, 27],
  'cabinet-resurface': [30, 45, 38],
  'drywall-repair':    [20, 30, 25],
  'deck-staining':     [22, 32, 27],
  'bathtub-resurface': [30, 45, 38],
  'bathtub-enclosure': [28, 42, 35],
  'counter-tops':      [25, 40, 32],
  'electrician':       [25, 40, 32],
  'plumbing':          [28, 42, 35],
  'roofing':           [15, 25, 20],
  'windows':           [18, 28, 22],
  'lawn-care':         [30, 50, 40],
  'cleaning':          [35, 55, 45],
  'special':           [25, 40, 30],
  'custom':            [20, 35, 28],
};

export const suggestMarkup = (slug, subtotal) => {
  const [floor, ceiling, sweet] = TRADE_BASE_MARKUP[slug] ?? [20, 35, 28];
  const sub = Number.isFinite(subtotal) ? subtotal : 0;

  // Job-size band:
  //   small  ($0–$1,500):    bias toward ceiling (overhead heavy)
  //   medium ($1,500–$10k):  sweet spot
  //   large  ($10k+):        bias toward floor (win more by being competitive)
  let suggested;
  let band;
  if (sub < 1500) {
    suggested = Math.round((sweet + ceiling) / 2);
    band = 'small';
  } else if (sub < 10000) {
    suggested = sweet;
    band = 'medium';
  } else {
    suggested = Math.round((floor + sweet) / 2);
    band = 'large';
  }

  const sizeLabel = {
    small:  'small job — overhead-heavy, bumped toward ceiling',
    medium: 'mid-sized job — sweet spot',
    large:  'large job — leaner markup wins more bids',
  }[band];

  return {
    percent: suggested,
    range: [floor, ceiling],
    rationale: `${sizeLabel}. Industry typical for this trade: ${floor}–${ceiling}%.`,
  };
};
